// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductAvailabilityStyles } from "@gcg/Overrides/Components/ProductAvailability";
import { ProductPriceStyles } from "@gcg/Overrides/Components/ProductPrice";
import { screen } from "@gcg/XDL/Helpers";
import color from "@gcg/XDL/Utilities/color";
import printStyles, { onPrint } from "@gcg/XDL/Utilities/print";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import { DataTableHeaderPresentationProps } from "@insite/mobius/DataTable/DataTableHeader";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Search from "@insite/mobius/Icons/Search";
import { LazyImageProps } from "@insite/mobius/LazyImage/LazyImage";
import { PaginationPresentationProps } from "@insite/mobius/Pagination";
import { ContentBodyProps, OverflowWrapperProps, PopoverPresentationProps } from "@insite/mobius/Popover";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";

import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

export interface ProductDetailsVariantOptionsStyles {
    wrapper?: InjectableCss;
    heading?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    paginationWrapper?: InjectableCss;
    paginationMessage?: TypographyPresentationProps;
    pagination?: PaginationPresentationProps;
    searchInput?: TextFieldPresentationProps;
    tableWrapper?: InjectableCss;
    tableHead?: DataTableHeadProps;
    tableHeaderCell?: DataTableHeaderPresentationProps;
    tableRow?: DataTableRowProps;
    selectedTableRow?: DataTableRowProps;
    actionsCell?: DataTableCellProps;
    centeredCell?: DataTableCellProps;
    inventoryCell?: DataTableCellProps;
    imageCell?: DataTableCellProps;
    image?: LazyImageProps;
    availabilityExtendedStyles?: ProductAvailabilityStyles;
    priceExtendedStyles?: ProductPriceStyles;
    productActionsWrapper?: InjectableCss;
    extendedQtyFieldStyles?: TextFieldPresentationProps;
    extendedAddToCartStyles?: InjectableCss;
    qtyPopoverIcon?: IconPresentationProps;
    qtyPopover?: PopoverPresentationProps;
    qtyPopoverWrapper?: OverflowWrapperProps;
    qtyPopoverContentBody?: ContentBodyProps;
    labelText?: TypographyPresentationProps;
    valueText?: TypographyPresentationProps;
}

const priceTextStyle: InjectableCss = {
    css: css`
        font-size: 18px;
        font-weight: 600;
        line-height: 22px;
    `,
};

const tdToBlock = css`
    ${screen("lte", "sm")`
        display: block;
        float: left;
        width: 50%;
        height: auto;
        text-align: left;
        padding: 0;
    `}
`;

const tableRowStyle = css`
    cursor: default;
    height: 105px;

    &:nth-child(even) {
        &, &:hover {
            background-color: ${getColor("common.white")};
        }
    }

    &:nth-child(odd) {
        &, &:hover {
            background-color: ${getColor("common.ui3")};
        }
    }

    > td {
        border: none;
        ${tdToBlock}
    }

    ${screen("lte", "sm")`
        height: auto;
        display: block;
        padding: 15px;

        &:before, &:after {
            content: "";
            clear: both;
            width: 100%;
            display: block;
        }
    `}

    ${printStyles.avoidBreakInside}

    ${onPrint(css`
        border-bottom: 1px solid ${getColor("common.border")};
    `)}
`;

const productOptionsStyles: ProductDetailsVariantOptionsStyles = {
    wrapper: {
        css: css`
            margin: 20px 0;

            ${screen("lte", "sm")`
                margin: 40px 0;
            `}
        `,
    },
    heading: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            margin-bottom: 20px;

            ${screen("lte", "sm")`
                align-items: flex-start;
            `}

            ${screen("lte", "xs")`
                flex-wrap: wrap;
            `}
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            align-items: center;
            white-space: nowrap;

            ${screen("lte", "md")`
                flex-wrap: wrap;
                flex: 1 1 50%;
            `}
        `,
    },
    title: {
        variant: "h3",
        size: 32,
        lineHeight: "38px",
        weight: "normal",
        css: css`
            margin: 0 35px 0 0;
            text-transform: uppercase;

            ${screen("lte", "md")`
                font-size: 24px;
                line-height: 28px;
                margin: 0 0 15px 0;
            `}
        `,
    },
    paginationWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: center;

            ${screen("lte", "md")`
                flex: 1 1 50%;
                justify-content: flex-end;
            `}
        `,
    },
    paginationMessage: {
        size: 14,
        lineHeight: "17px",
        css: css`
            display: block;
            margin-right: 20px;

            ${screen("lte", "sm")`
                order: 1;
                font-size: 12px;
                line-height: 14px;
                margin-right: 0px;
                flex: 1 1 100%;
                margin-top: 25px;
                text-align: right;
            `}

            ${screen("lte", "xs")`
                order: 0;
                flex: 1 1 auto;
                margin: 0 20px 0 0;
            `}
        `,
    },
    pagination: {
        cssOverrides: {
            pagination: css`
                ${screen("lte", "xs")`
                    white-space: nowrap;
                `}

                ${printStyles.hideOnPrint}
            `,
        },
    },
    searchInput: {
        iconProps: {
            src: Search,
            color: "common.border",
            css: css`
                padding: 0 !important;
                top: 50% !important;
                transform: translateY(-50%);
                left: 10px !important;
            `,
        },
        cssOverrides: {
            formInputWrapper: css`
                position: relative;

                ${screen("lte", "xs")`
                    margin-bottom: 10px;
                `}

                ${printStyles.hideOnPrint}
            `,
            inputSelect: css`
                width: 250px;

                &, &:focus {
                    padding-left: 35px;
                }

                ${screen("lte", "md")`
                    font-size: 12px;
                    lineHeight: 14px;
                `}

                ${screen("lte", "sm")`
                    height: 32px;
                    width: 200px;
                `}

                ${screen("lte", "xs")`
                    width: 100%;
                `}
            `,
        },
    },
    tableWrapper: {
        css: css`
            margin-bottom: 20px;

            @media (max-width: 800px) {
                overflow-x: auto;
            }
        `,
    },
    tableHead: {
        css: css`
            color: ${getColor("common.white")};
            background-color: ${getColor("text")};
            border: none;

            ${screen("lte", "sm")`
                display: none;
            `}
        `,
    },
    tableHeaderCell: {
        sortClickableProps: {
            css: css`
                color: ${getColor("common.white")};
                margin: auto;
            `,
        },
        sortIconProps: {
            color: `${getColor("common.white")}`,
        },
        typographyProps: {
            color: "common.white",
        },
        css: css`
            position: relative;
            background-color: ${getColor("text")};
            border: none;
            height: 50px;
            text-align: center;
            position: sticky;
            top: 75px;
            z-index: 1;

            @media (max-width: 800px) {
                position: static;
            }

            &::after {
                position: absolute;
                top: 50%;
                right: 0;
                transform: translateY(-50%);
                content: "";
                display: block;
                width: 1px;
                height: 32px;
                background-color: ${getColor("common.accentContrast")};
            }
        `,
    },
    tableRow: {
        css: tableRowStyle,
    },
    selectedTableRow: {
        css: css`
            box-shadow: inset 0 0 0 5px ${getColor("primary")};
            ${tableRowStyle}
        `,
    },
    actionsCell: {
        css: css`
            width: 230px;
            padding: 8px 12px;
            && {
                ${screen("lte", "sm")`
                    display: flex;
                    flex-wrap: wrap;
                    justify-content: space-between;
                    align-items: center;
                    margin-top: 25px;
                    width: 100%;
                `}
            }
        `,
    },
    centeredCell: {
        css: css`
            text-align: center;

            ${screen("lte", "sm")`
                && {
                    display: flex;
                }
                font-size: 12px;
                line-height: 14px;
                margin-bottom: 8px;
            `}
        `,
    },
    imageCell: {
        css: css`
            width: 150px;
            max-width: 150px;
        `,
    },
    image: {
        width: "95px",
        height: "95px",
        css: css`
            ${screen("lte", "sm")`
                width: 125px;
                height: 125px;
            `}
        `,
    },
    inventoryCell: {
        css: css`
            ${screen("lte", "sm")`
                margin-top: 15px;
            `}
        `,
    },
    availabilityExtendedStyles: {
        messageText: {
            css: css`
                ${screen("lte", "sm")`
                    font-size: 12px;
                    line-height: 14px;
                `}
            `,
        },
        availabilityByWarehouseLink: {
            css: css`
                display: none;
            `,
        },
    },
    priceExtendedStyles: {
        wrapper: {
            css: css`
                margin-bottom: 15px;

                ${screen("lte", "sm")`
                    margin: 0 10px 0 0;
                `}
            `,
        },
        price: {
            priceText: priceTextStyle,
            realTimeText: priceTextStyle,
            unitOfMeasureText: priceTextStyle,
        },
        packWrapper: {
            css: css`
                display: none;
            `,
        },
        quoteMessage: { text: priceTextStyle },
    },
    productActionsWrapper: {
        css: css`
            display: flex;
            align-items: center;
            width: 230px;

            ${screen("lte", "sm")`
                justify-content: flex-end;
                width: auto;
            `}
            ${printStyles.hideOnPrint}
        `,
    },
    extendedQtyFieldStyles: {
        cssOverrides: {
            formField: css`
                margin-right: 20px;
                height: 32px;
                flex: 0 0 80px;
                text-align: center;

                ${screen("lte", "sm")`
                    margin-right: 10px;
                    flex: 0 0 65px;
                `}
            `,
            inputSelect: css`
                height: 32px;
                text-align: center;
            `,
        },
    },
    extendedAddToCartStyles: {
        css: css`
            height: 32px;
            min-height: 32px !important;
            flex: 0 0 80px;
            margin-right: 20px;
            line-height: 10px;
        `,
    },
    qtyPopoverIcon: {
        size: 20,
    },
    qtyPopover: {
        transitionDuration: "short",
        toggle: false,
        xPosition: "start",
        yPosition: "bottom",
    },
    qtyPopoverWrapper: {
        as: "div",
        _height: "auto",
        css: css`
            position: static;
            width: 16px;
            flex: 0 0 32px;
            display: flex;
            align-items: center;
        `,
    },
    qtyPopoverContentBody: {
        as: "div",
        _height: "100%",
        css: css`
            font-size: 12px;
            letter-spacing: 0;
            line-height: 18px;
            padding: 15px;
            box-shadow: 0 4px 8px 0 ${color("common.backgroundContrast", 0.2)};
            position: absolute !important;
            top: auto !important;
            bottom: 100% !important;
            right: 0 !important;
            left: auto !important;
            overflow: hidden;
            height: auto;
            width: auto;
            max-width: none;
            max-height: none;
        `,
    },
    labelText: {
        size: 14,
        lineHeight: "18px",
        weight: "bold",
        css: css`
            margin-right: 2px;
        `,
    },
    valueText: {
        size: 14,
        lineHeight: "18px",
        css: css`
            &:last-child {
                margin-bottom: 0;
            }
        `,
    },
};

export default productOptionsStyles;
